import { useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  APPLICATION_PAGE,
  JOB_SHARE_REF,
  captureJobLinkOrigin,
  setApplicationPageAndType,
} from '@/features/jobs/utils/jobOrigin';
import { PageLayout } from '@/components/layout/PageLayout';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { Toast } from '@/features/home/components/Toast';
import { analyticsService } from '@/services/analytics/analyticsService';
import { ShareJobMenu } from './components/ShareJobMenu';
import { EasyApplyButton } from './components/EasyApplyButton';
import { RelevanceMatchBadge } from './components/RelevanceMatchBadge';
import {
  ExperienceIcon,
  FlagIcon,
  JobApplyActions,
  LocationIcon,
  jobCardStyles,
} from './components/JobCardParts';
import { useJobDetails } from './hooks/useJobDetails';
import { useLongPressCopy } from './utils/longPressCopy';
import { useTenantStore } from '@/store/tenantStore';
import { getJobDetailsHeaderImageUrl } from '@/services/tenant/tenantImages';
import {
  formatExperience,
  formatPostedDate,
  formatJobLocation,
  buildJobShareUrl,
  formatString,
} from './utils/jobHelpers';
import { ROUTES } from '@/app/router/routes';
import type { JobEntity } from '@/types/job.types';
import styles from './JobDetailPage.module.css';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

/**
 * JobDetailPage — /jobs/:id. Mirrors Flutter JobDetailsView: hero card (title, relevance match,
 * save / share, Job ID, location, experience, posted date, apply buttons), the job sections,
 * and the similar jobs. Long-pressing the title or the Job ID copies it.
 */

// ---------------------------------------------------------------------------
// Similar Job Card — Flutter SimiliarJobCard (similar_job_card.dart)
// ---------------------------------------------------------------------------

interface SimilarJobCardProps {
  job: JobEntity;
  enableEasyApply: boolean;
  onTitleClick: () => void;
  onSave: () => void;
  onUnsave: () => void;
  onApply: (isEasyApply: boolean) => void;
}

function SimilarJobCard({
  job,
  enableEasyApply,
  onTitleClick,
  onSave,
  onUnsave,
  onApply,
}: SimilarJobCardProps) {
  const { t } = useTranslation();
  const jobTitle = job.jobTitle ?? '';
  const displayId = job.displayId ?? '';
  const location = formatJobLocation(job) || 'N/A';
  const experience = formatExperience(
    job.requiredExperienceInMonths?.from,
    job.requiredExperienceInMonths?.to,
  );
  const longPressTitle = useLongPressCopy(jobTitle);

  return (
    <div className={styles.similarJobCard}>
      <div className={styles.similarJobHead}>
        <div
          className={styles.similarJobTitle}
          role="link"
          tabIndex={0}
          title={jobTitle}
          onClick={onTitleClick}
          onKeyDown={(e) => e.key === 'Enter' && onTitleClick()}
          {...longPressTitle}
        >
          {jobTitle}
        </div>
        <button
          className={`${styles.similarJobSaveBtn} ${job.isSavedJob ? styles.similarJobSaveBtnSaved : ''}`}
          onClick={job.isSavedJob ? onUnsave : onSave}
          disabled={job.isLoadingForSaveJobButton}
          title={job.isSavedJob ? t('Unsave job') : t('Save job')}
          type="button"
          aria-label={job.isSavedJob ? t('Unsave job') : t('Save job')}
        >
          {job.isLoadingForSaveJobButton ? (
            <span className={jobCardStyles.spinner} />
          ) : (
            <svg width="16" height="16" viewBox="0 0 20 20" fill={job.isSavedJob ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
              <path d="M5 3.5h10v13l-5-3.5-5 3.5Z"/>
            </svg>
          )}
        </button>
        <ShareJobMenu shareUrl={buildJobShareUrl(displayId)} jobId={job.id ?? ''} jobTitle={jobTitle} />
      </div>
      <ul className={styles.similarJobFacts}>
        <li className={styles.similarJobFact}>
          <span title={t('Job ID')}><FlagIcon /></span>
          <span>{t('Job ID')} {displayId}</span>
        </li>
        <li className={styles.similarJobFact}>
          <span title={t('Location')}><LocationIcon /></span>
          <span>{location}</span>
        </li>
      </ul>
      <div className={styles.similarJobActions}>
        <span className={styles.similarJobFact}>
          <span title={t('Experience')}><ExperienceIcon /></span>
          <span>{experience}</span>
        </span>
        <div className={styles.similarJobButtons}>
          {/* Flutter SimiliarJobCard: only isApplied counts as applied */}
          <JobApplyActions
            isApplied={!!job.isApplied}
            enableEasyApply={enableEasyApply}
            onApply={onApply}
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shimmers — same layout as the loaded hero card, sections and similar-job cards
// ---------------------------------------------------------------------------

function HeroShimmer() {
  return (
    <div className={`${styles.heroCard} ${styles.heroShimmer}`} aria-hidden="true">
      <div className={styles.heroTopRow}>
        <span className={`${styles.heroShimmerBar} ${styles.shimmerHeroTitle}`} />
        <div className={styles.heroActions}>
          <span className={`${styles.heroShimmerBar} ${styles.shimmerIconBtn}`} />
          <span className={`${styles.heroShimmerBar} ${styles.shimmerIconBtn}`} />
        </div>
      </div>
      <div className={styles.heroMetaRow}>
        <span className={`${styles.heroShimmerBar} ${styles.shimmerMeta}`} />
        <span className={`${styles.heroShimmerBar} ${styles.shimmerMetaWide}`} />
      </div>
      <div className={styles.heroBottomRow}>
        <div className={styles.heroBottomMeta}>
          <span className={`${styles.heroShimmerBar} ${styles.shimmerMetaShort}`} />
          <span className={`${styles.heroShimmerBar} ${styles.shimmerMeta}`} />
        </div>
        <div className={styles.applyBtnGroup}>
          <span className={`${styles.heroShimmerBar} ${styles.shimmerBtnWide}`} />
          <span className={`${styles.heroShimmerBar} ${styles.shimmerBtn}`} />
        </div>
      </div>
    </div>
  );
}

function ContentShimmer({ lines }: { lines: number }) {
  return (
    <section className={styles.contentShimmer} aria-hidden="true">
      <span className={`${styles.shimmerBlock} ${styles.shimmerSectionTitle}`} />
      {Array.from({ length: lines }).map((_, i) => (
        <span key={i} className={`${styles.shimmerBlock} ${styles.shimmerLine}`} />
      ))}
    </section>
  );
}

function SimilarJobsShimmer() {
  return (
    <div aria-hidden="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={`${styles.similarJobCard} ${styles.similarJobShimmer}`}>
          <span className={`${styles.shimmerBlock} ${styles.shimmerCardTitle}`} />
          <span className={`${styles.shimmerBlock} ${styles.shimmerCardMeta}`} />
          <div className={styles.similarJobActions}>
            <span className={`${styles.shimmerBlock} ${styles.shimmerCardLink}`} />
            <span className={`${styles.shimmerBlock} ${styles.shimmerCardIcon}`} />
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// JobDetailPage
// ---------------------------------------------------------------------------

function toNumber(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : undefined;
}

export function JobDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const preferences = useTenantStore((s) => s.preferences);
  const config = useTenantStore((s) => s.config);
  const content = useTenantStore((s) => s.content);
  const { state, actions } = useJobDetails(id ?? '');
  const isMobile = useIsMobile();

  // channel / sourceName / ref → referral headers + application origin (Flutter jobsRoute)
  useEffect(() => {
    if (id) captureJobLinkOrigin(searchParams);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    jobDetails,
    similarJobs,
    isLoading,
    similarJobsLoading,
    jobClosedOrInactive,
    requiredSkills,
    otherSkills,
    relevance,
    jobDetailsAdditionalSection,
    toast,
  } = state;

  const longPressTitle = useLongPressCopy(jobDetails?.jobTitle ?? '');
  const longPressJobId = useLongPressCopy(jobDetails?.displayId ?? '');

  const shareUrl = buildJobShareUrl(jobDetails?.displayId ?? '');

  // Hero card background — Flutter _buildJobInfoCardTopWidget DecorationImage(jobDetailsHeaderImage).
  // The brand colour from CSS stays underneath and shows if the image fails to load.
  const heroImageUrl = getJobDetailsHeaderImageUrl(content);
  const heroStyle = heroImageUrl ? { backgroundImage: `url("${heroImageUrl}")` } : undefined;

  // Tenant config `enableEasyApply`
  const enableEasyApply = !!(config as Record<string, unknown> | null)?.enableEasyApply;

  // Apply buttons — Flutter _buildApplyActionButton. Of preference jobdetailsPage.applyButton,
  // Flutter applies only textSize and buttonHeight, on desktop: it also reads backgroundColor,
  // textColor and textFontWeight but never passes them to the buttons, which stay
  // primaryColorDark (--accent) with white text. Mobile uses fixed sizes.
  const applyButtonConfig = (
    (preferences as Record<string, unknown> | null)?.jobdetailsPage as Record<string, unknown> | undefined
  )?.applyButton as Record<string, unknown> | undefined;
  const textSize = toNumber(applyButtonConfig?.textSize);
  const buttonHeight = toNumber(applyButtonConfig?.buttonHeight);
  const desktopButtonStyle: CSSProperties = {
    height: buttonHeight ?? 40,
    ...(textSize !== undefined ? { fontSize: textSize } : {}),
  };
  const applyButtonStyle: CSSProperties = isMobile ? { height: 35, fontSize: 14 } : desktopButtonStyle;
  const appliedButtonStyle: CSSProperties = isMobile ? { height: 40, fontSize: 15 } : desktopButtonStyle;

  // Flutter job-details Apply / Easy Apply: new tab, analyticsClass job_details_page. The
  // application origin is left as it is (Flutter only navigates), so the origin stored when the
  // page was opened — career-job-share from a shared link, career-search from the list — is sent.
  const handleApply = useCallback(
    (isEasyApply: boolean) => {
      if (!jobDetails) return;
      const route = isEasyApply ? ROUTES.EASY_APPLY : ROUTES.APPLICATION;
      void analyticsService.logScreenView({ screenName: route, screenClass: 'job_details_page' });
      window.open(`${route}/${jobDetails.displayId ?? ''}`, '_blank');
    },
    [jobDetails],
  );

  // Flutter SimilarJobsList onTapTitle: new tab with the internal-link ref (no analyticsClass)
  const handleSimilarJobTitleClick = useCallback((job: JobEntity) => {
    void analyticsService.logScreenView({ screenName: ROUTES.JOBS });
    window.open(`${ROUTES.JOBS}/${job.displayId ?? ''}?ref=${JOB_SHARE_REF.INTERNAL}`, '_blank');
  }, []);

  // Flutter SimilarJobsList onApply: career-recommendation origin, new tab
  const handleSimilarJobApply = useCallback((job: JobEntity, isEasyApply: boolean) => {
    setApplicationPageAndType(APPLICATION_PAGE.CAREER_RECOMMENDATION, false);
    const route = isEasyApply ? ROUTES.EASY_APPLY : ROUTES.APPLICATION;
    void analyticsService.logScreenView({
      screenName: route,
      screenClass: isEasyApply
        ? 'job_details_page_similar_jobs_easy_apply'
        : 'job_details_page_similar_jobs',
    });
    window.open(`${route}/${job.displayId ?? ''}`, '_blank');
  }, []);

  if (!id) return <Navigate to={ROUTES.JOBS} replace />;

  const relevanceLevel = relevance && 'level' in relevance ? relevance.level : undefined;

  // Flutter: only isApplied counts as applied on the job page (an Easy Apply still offers Apply)
  const applyActions = jobDetails ? (
    <div className={styles.applyBtnGroup}>
      {jobDetails.isApplied ? (
        <button
          type="button"
          className={`${styles.applyBtn} ${styles.applyBtnPrimary} ${styles.appliedBtn}`}
          style={appliedButtonStyle}
          disabled
        >
          {t('Applied')}
        </button>
      ) : (
        <>
          {enableEasyApply && (
            <EasyApplyButton
              className={styles.heroEasyApplyBtn}
              style={applyButtonStyle}
              onClick={() => handleApply(true)}
            />
          )}
          <button
            className={`${styles.applyBtn} ${styles.applyBtnPrimary}`}
            onClick={() => handleApply(false)}
            type="button"
            style={applyButtonStyle}
          >
            {t('Apply')}
          </button>
        </>
      )}
    </div>
  ) : null;

  return (
    <PageLayout>
      <div className={styles.container}>
        {/* ── Hero card ── */}
        {isLoading ? (
          <HeroShimmer />
        ) : jobDetails ? (
          <div className={styles.heroCard} style={heroStyle}>
            {/* Row 1: Title + relevance + actions */}
            <div className={styles.heroTopRow}>
              <h1 className={styles.heroTitle} title={jobDetails.jobTitle ?? 'N/A'} {...longPressTitle}>
                {jobDetails.jobTitle ?? 'N/A'}
              </h1>
              <div className={styles.heroActions}>
                {relevanceLevel !== undefined && <RelevanceMatchBadge level={relevanceLevel} />}
                {/* Save button */}
                <button
                  className={`${styles.heroActionBtn} ${jobDetails.isSavedJob ? styles.heroActionBtnSaved : ''}`}
                  onClick={() =>
                    jobDetails.isSavedJob
                      ? actions.onUnsaveJob(jobDetails)
                      : actions.onSaveJob(jobDetails, false)
                  }
                  title={jobDetails.isSavedJob ? t('Unsave job') : t('Save job')}
                  type="button"
                  aria-label={jobDetails.isSavedJob ? t('Unsave job') : t('Save job')}
                >
                  <svg width="16" height="16" viewBox="0 0 20 20" fill={jobDetails.isSavedJob ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.5">
                    <path d="M5 3.5h10v13l-5-3.5-5 3.5Z"/>
                  </svg>
                </button>
                {/* Share — same tenant-configured options as the job cards */}
                <ShareJobMenu
                  shareUrl={shareUrl}
                  jobId={jobDetails.id ?? ''}
                  jobTitle={jobDetails.jobTitle ?? ''}
                  triggerClassName={styles.heroShareTrigger}
                />
              </div>
            </div>

            {/* Row 2: Job ID + Location */}
            <div className={styles.heroMetaRow}>
              <span className={styles.heroMetaItem}>
                <span title={t('Job ID')} className={styles.heroMetaIconWrap}>
                  <svg className={styles.heroMetaIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="3" width="14" height="14" rx="2"/><path d="M7 7h6M7 10h4"/></svg>
                </span>
                <span {...longPressJobId}>
                  {t('Job ID')} {jobDetails.displayId ?? ''}
                </span>
              </span>
              <span className={styles.heroMetaItem}>
                <span title={t('Location')} className={styles.heroMetaIconWrap}>
                  <svg className={styles.heroMetaIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M10 10.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z"/><path d="M10 18s-6-5.7-6-9.5a6 6 0 1112 0C16 12.3 10 18 10 18z"/></svg>
                </span>
                {formatJobLocation(jobDetails) || 'N/A'}
              </span>
            </div>

            {/* Row 3: Experience + Posted + Apply (desktop) */}
            <div className={styles.heroBottomRow}>
              <div className={styles.heroBottomMeta}>
                <span className={styles.heroMetaItem}>
                  <span title={t('Experience')} className={styles.heroMetaIconWrap}>
                    <svg className={styles.heroMetaIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="2" y="5" width="16" height="12" rx="2"/><path d="M6 5V3a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                  </span>
                  {formatExperience(
                    jobDetails.requiredExperienceInMonths?.from,
                    jobDetails.requiredExperienceInMonths?.to,
                  ) || 'N/A'}
                </span>
                <span className={styles.heroMetaItem}>
                  <span title={t('Posted on')} className={styles.heroMetaIconWrap}>
                    <svg className={styles.heroMetaIcon} viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5"><rect x="3" y="4" width="14" height="14" rx="2"/><path d="M3 8h14M7 2v4M13 2v4"/></svg>
                  </span>
                  {t('Posted')} {formatPostedDate(jobDetails.jobPosting?.startDate, t)}
                </span>
              </div>

              {!isMobile && applyActions}
            </div>

            {/* Mobile: apply buttons on their own row, aligned right */}
            {isMobile && <div className={styles.mobileApplyRow}>{applyActions}</div>}
          </div>
        ) : null}

        {/* ── Body (two-column on desktop, single on mobile) ── */}
        <div className={styles.bodyLayout}>
          <div className={styles.bodyMain}>
            {isLoading ? (
              <>
                <ContentShimmer lines={5} />
                <ContentShimmer lines={7} />
                <ContentShimmer lines={3} />
              </>
            ) : jobDetails ? (
              <>
                {/* About the Company */}
                {jobDetails.aboutCompany && (
                  <section>
                    <h2 className={styles.sectionTitle}>{t('About the Company')}</h2>
                    <div
                      className={styles.sectionContent}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(jobDetails.aboutCompany) }}
                    />
                  </section>
                )}

                {/* Job Information / Description */}
                {jobDetails.jobDescription && (
                  <section>
                    <h2 className={styles.sectionTitle}>{t('Job Information')}</h2>
                    <div
                      className={styles.sectionContent}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(jobDetails.jobDescription) }}
                    />
                  </section>
                )}

                {/* Required Skills */}
                {requiredSkills.length > 0 && (
                  <section>
                    <h2 className={styles.sectionTitle}>{t('Required Skills')}</h2>
                    <div className={styles.skillsWrap}>
                      {requiredSkills.map((skill, i) => (
                        <span key={`${skill}-${i}`} className={styles.skillChip}>{skill}</span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Other Skills */}
                {otherSkills.length > 0 && (
                  <section>
                    <h2 className={styles.sectionTitle}>{t('Other Skills')}</h2>
                    <div className={styles.skillsWrap}>
                      {otherSkills.map((skill, i) => (
                        <span key={`${skill}-${i}`} className={styles.skillChip}>{skill}</span>
                      ))}
                    </div>
                  </section>
                )}

                {/* Required Education */}
                {jobDetails.requiredEducation && jobDetails.requiredEducation.length > 0 && (
                  <section>
                    <h2 className={styles.sectionTitle}>{t('Required Education')}</h2>
                    <div className={styles.sectionContent}>
                      {jobDetails.requiredEducation
                        .map((ed) => ed.degreeName)
                        .filter(Boolean)
                        .join(', ')}
                    </div>
                  </section>
                )}

                {/* Job Type — the values go through the language file too */}
                {(jobDetails.employmentType || jobDetails.jobShiftType) && (
                  <section>
                    <h2 className={styles.sectionTitle}>{t('Job Type')}</h2>
                    {jobDetails.employmentType && (
                      <div className={styles.jobTypeRow}>
                        <span className={styles.jobTypeLabel}>{t('Employee status')} : </span>
                        {t(formatString(jobDetails.employmentType))}
                      </div>
                    )}
                    {jobDetails.jobShiftType && (
                      <div className={styles.jobTypeRow}>
                        <span className={styles.jobTypeLabel}>{t('Shift')} : </span>
                        {t(formatString(jobDetails.jobShiftType))}
                      </div>
                    )}
                    <div style={{ marginBottom: 32 }} />
                  </section>
                )}

                {/* Additional sections from tenant preferences (label + description translated) */}
                {jobDetailsAdditionalSection.map((item, idx) => (
                  <section key={idx}>
                    <h2 className={styles.sectionTitle}>{t(item.label ?? '')}</h2>
                    <div className={styles.sectionContent}>{t(item.description ?? '')}</div>
                  </section>
                ))}
              </>
            ) : null}
          </div>

          {/* Sidebar — Similar Jobs (heading always shown, as in Flutter) */}
          <aside className={styles.bodySidebar}>
            <h3 className={styles.sidebarTitle}>{t('Similar Jobs')}</h3>

            {similarJobsLoading ? (
              <SimilarJobsShimmer />
            ) : similarJobs.length === 0 ? (
              <div className={styles.noSimilarJobs}>
                <svg className={styles.noSimilarJobsIcon} width="40" height="40" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" aria-hidden="true">
                  <circle cx="9" cy="9" r="6" />
                  <path d="M13.5 13.5 17 17M6.5 9h5" />
                </svg>
                {t('No Similar Jobs')}
              </div>
            ) : (
              <>
                {similarJobs.slice(0, 5).map((job, index) => (
                  <SimilarJobCard
                    key={job.id ?? index}
                    job={job}
                    enableEasyApply={enableEasyApply}
                    onTitleClick={() => handleSimilarJobTitleClick(job)}
                    onSave={() => actions.onSaveJob(job, true, index)}
                    onUnsave={() => actions.onUnsaveJob(job, index)}
                    onApply={(isEasyApply) => handleSimilarJobApply(job, isEasyApply)}
                  />
                ))}
              </>
            )}
          </aside>
        </div>

        {/* ── Job Closed Dialog — Flutter CustomDialog.showWarningDialog ── */}
        {jobClosedOrInactive && (
          <div className={styles.dialogOverlay} role="dialog" aria-modal="true">
            <div className={styles.dialogCard}>
              <p className={styles.dialogText}>
                {t("Oops! The job you're looking for doesn't seem to exist or may have been removed. Feel free to explore other opportunities on our homepage")}
              </p>
              <button
                className={styles.dialogBtn}
                onClick={actions.dismissJobClosed}
                type="button"
              >
                {t('Go to Home Page')}
              </button>
            </div>
          </div>
        )}

        {/* Toast */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={actions.clearToast}
          />
        )}
      </div>
    </PageLayout>
  );
}
